// Pipeline-state derivation functions extracted from pipeline-state-helpers.ts.
//
// Each function is a pure transformation of its inputs — no React, no hooks,
// no side effects. The hook calls these inside its useMemo callbacks so the
// memoization semantics (and hook call order) are preserved exactly.

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";
import type { PipelineRunRow } from "src/domains/application/hooks/usePipelineRunsApi";
import type { VcsConnectionStatus } from "src/domains/application/hooks/useVcsConnectionStatus";

import {
  PIPELINE_STEPS,
  getStepStatus,
  stepConfig,
} from "../pipeline-tab-helpers";
import type { StageInfo, StageKey } from "../StagesSidebar";

import {
  DB_STATUS_TO_STAGE_STATUS,
  DEFAULT_STAGE_STATUS,
  STAGE_DEFINITIONS,
} from "./pipeline-state-constants";

// ── Effective pipeline state ─────────────────────────────────────────────────

export interface EffectivePipelineStateParams {
  filteredPipelineState: PipelineStateStep[];
  syntheticPendingState: PipelineStateStep[];
  pipelineState: PipelineStateStep[];
  selectedRunId: string | null;
  latestSagaId: string | null;
}

/**
 * Resolve the effective pipeline state used by the DAG.
 *
 * Order of preference:
 *   1. Per-service rows (filtered) when available
 *   2. Synthetic pending state for queued/in_progress runs
 *   3. Phase-level rows from the full pipelineState (filteredPipelineState
 *      strips these, but they carry authoritative status)
 */
export function computeEffectivePipelineState({
  filteredPipelineState,
  syntheticPendingState,
  pipelineState,
  selectedRunId,
  latestSagaId,
}: EffectivePipelineStateParams): PipelineStateStep[] {
  // Prefer per-service rows (filtered) when available
  if (filteredPipelineState.length > 0) {
    return filteredPipelineState;
  }
  // Synthetic pending state for queued/in_progress runs
  if (syntheticPendingState.length > 0) {
    return syntheticPendingState;
  }
  // Fallback: include phase-level rows from the full pipelineState
  // (filteredPipelineState strips these, but they carry authoritative status)
  if (pipelineState.length > 0) {
    const targetSaga = selectedRunId ?? latestSagaId;
    const scoped = targetSaga
      ? pipelineState.filter((s) => s.sagaId === targetSaga)
      : pipelineState;
    if (scoped.length > 0) return scoped;
  }
  return filteredPipelineState;
}

// ── Global filtered pipeline state ───────────────────────────────────────────

/**
 * Compute the global pipeline state (not scoped to a single run) excluding
 * phase-level rows. Scoped to the latest sagaId so past-run failures don't
 * pollute the badge.
 */
export function computeGlobalFilteredPipelineState(
  pipelineState: PipelineStateStep[],
  targetSaga: string | null,
): PipelineStateStep[] {
  if (pipelineState.length === 0) return pipelineState;
  return pipelineState.filter(
    (s) =>
      (!targetSaga || s.sagaId === targetSaga) &&
      !(
        s.serviceName === "" &&
        (s.step === "build" || s.step === "deploy" || s.step === "vcs")
      ),
  );
}

// ── Pipeline status ──────────────────────────────────────────────────────────

export interface PipelineStatusParams {
  pipelineState: PipelineStateStep[];
  globalFilteredPipelineState: PipelineStateStep[];
  events: DeploymentEvent[];
  selectedApiRun: PipelineRunRow | null;
}

/**
 * Compute the overall pipeline status badge value.
 *
 * Uses the materialized pipeline_state view when available, falling back to
 * events. VCS is decoupled — its status comes from VcsConnection API.
 */
export function computePipelineStatus({
  pipelineState,
  globalFilteredPipelineState,
  events,
  selectedApiRun,
}: PipelineStatusParams): string {
  if (pipelineState.length > 0) {
    if (globalFilteredPipelineState.length === 0) {
      // No pipeline steps for this run yet — check if the selected API run
      // is queued/in_progress to show the correct status badge
      if (
        selectedApiRun &&
        (selectedApiRun.status === "queued" ||
          selectedApiRun.status === "in_progress")
      ) {
        return selectedApiRun.status === "queued"
          ? "Queued"
          : stepConfig.build.inProgressLabel;
      }
      return "idle";
    }
    for (const step of PIPELINE_STEPS) {
      const serviceRows = globalFilteredPipelineState.filter(
        (s) => s.step === step,
      );
      if (serviceRows.length > 0) {
        if (serviceRows.some((s) => s.status === "failed")) return "failed";
        if (serviceRows.some((s) => s.status === "in_progress"))
          return stepConfig[step].inProgressLabel;
        if (serviceRows.every((s) => s.status === "completed")) continue;
        return stepConfig[step].inProgressLabel;
      }
    }
    return "completed";
  }

  // No pipeline state at all — check API runs for queued status
  if (selectedApiRun?.status === "queued") return "Queued";
  if (selectedApiRun?.status === "in_progress")
    return stepConfig.build.inProgressLabel;

  // Fallback to events-based status
  if (events.length === 0) return "idle";
  for (const step of PIPELINE_STEPS) {
    const s = getStepStatus(step, events);
    if (s.status === "completed") continue;
    if (s.status === "in_progress") return stepConfig[step].inProgressLabel;
    if (s.status === "failed") return "failed";
  }
  return "completed";
}

// ── Build failed ─────────────────────────────────────────────────────────────

export function computeBuildFailed(
  globalFilteredPipelineState: PipelineStateStep[],
  events: DeploymentEvent[],
): boolean {
  if (globalFilteredPipelineState.length > 0) {
    const serviceBuildRows = globalFilteredPipelineState.filter(
      (s) => s.step === "build",
    );
    if (serviceBuildRows.length > 0) {
      return serviceBuildRows.some((s) => s.status === "failed");
    }
    return false;
  }
  return getStepStatus("build", events).status === "failed";
}

// ── All completed ────────────────────────────────────────────────────────────

export function computeAllCompleted(
  globalFilteredPipelineState: PipelineStateStep[],
): boolean {
  if (globalFilteredPipelineState.length > 0) {
    return globalFilteredPipelineState.every((s) => s.status === "completed");
  }
  return false;
}

// ── Stages derivation ────────────────────────────────────────────────────────

export interface DeriveStagesParams {
  globalFilteredPipelineState: PipelineStateStep[];
  pipelineState: PipelineStateStep[];
  selectedApiRun: PipelineRunRow | null;
  selectedRunId: string | null;
  latestSagaId: string | null;
  status?: string;
  vcsStatus: VcsConnectionStatus;
}

/**
 * Derive a status for a stage from per-service rows, with a fallback to the
 * phase-level row (serviceName === "") for the target sagaId when no
 * per-service rows exist.
 */
function deriveStageStatusFromRows(
  globalFilteredPipelineState: PipelineStateStep[],
  pipelineState: PipelineStateStep[],
  selectedRunId: string | null,
  latestSagaId: string | null,
  step: "build" | "deploy",
): StageInfo["status"] {
  if (globalFilteredPipelineState.length > 0) {
    const rows = globalFilteredPipelineState.filter(
      (s) => s.step === step && s.serviceName !== "",
    );
    if (rows.length > 0) {
      if (rows.some((s) => s.status === "failed")) return "failed";
      if (rows.some((s) => s.status === "in_progress")) return "in_progress";
      // Preserve the "queued" phase (e.g. a DEPLOY_QUEUED row before the
      // deploy-service picks the job up off the deploy-jobs stream).
      if (rows.some((s) => s.status === "queued")) return "queued";
      if (rows.every((s) => s.status === "completed")) return "completed";
      return "pending";
    }
  }
  // Fallback: use phase-level row (serviceName === "") for this sagaId
  // when no per-service rows exist.
  const targetSaga = selectedRunId ?? latestSagaId;
  if (targetSaga) {
    const phaseRow = pipelineState.find(
      (s) => s.step === step && s.serviceName === "" && s.sagaId === targetSaga,
    );
    if (phaseRow) {
      return phaseRow.status as StageInfo["status"];
    }
  }
  return "pending";
}

/**
 * Derive the four StagesSidebar stages (summary, setup, build, deploy) from
 * the current pipeline state, DB Application.status, and VCS connection status.
 */
export function deriveStages({
  globalFilteredPipelineState,
  pipelineState,
  selectedApiRun,
  selectedRunId,
  latestSagaId,
  status,
  vcsStatus,
}: DeriveStagesParams): StageInfo[] {
  // Build status — deploy-only runs implicitly completed build (a build must
  // have preceded the deploy for images to exist).
  const buildStatus: StageInfo["status"] =
    selectedApiRun?.trigger === "deploy"
      ? "completed"
      : deriveStageStatusFromRows(
          globalFilteredPipelineState,
          pipelineState,
          selectedRunId,
          latestSagaId,
          "build",
        );

  const deployStatus: StageInfo["status"] = deriveStageStatusFromRows(
    globalFilteredPipelineState,
    pipelineState,
    selectedRunId,
    latestSagaId,
    "deploy",
  );

  const setupStatus: StageInfo["status"] = vcsStatus.connected
    ? "completed"
    : vcsStatus.orgConnected
      ? "pending"
      : "pending";

  // Summary stage: DB Application.status is the single source of truth.
  // The pipelineStatus (derived from pipeline_state rows) can be stale when
  // events are lost. The DB status is authoritatively set by the API/deploy-service.
  const summaryStatus: StageInfo["status"] = (() => {
    if (!status) return "pending";
    const s = status.toLowerCase();
    return DB_STATUS_TO_STAGE_STATUS[s] ?? DEFAULT_STAGE_STATUS;
  })();

  const statuses: Record<StageKey, StageInfo["status"]> = {
    summary: summaryStatus,
    setup: setupStatus,
    build: buildStatus,
    deploy: deployStatus,
  };

  return STAGE_DEFINITIONS.map(({ key, label }) => ({
    key,
    label,
    status: statuses[key],
  }));
}
